import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Search, 
  Users, 
  User,
  Shield, 
  Star, 
  ArrowRight,
  MessageCircle,
  CheckCircle,
  Sparkles,
  UserPlus,
  Eye,
  Bell
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vivah`;

export default function VivahHomePage({ user }) {
  const navigate = useNavigate();
  const [myProfile, setMyProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentProfiles, setRecentProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchMyProfile();
      fetchStats();
      fetchRecentProfiles();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchMyProfile = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/profile/me?token=${token}`);
      setMyProfile(response.data);
    } catch (error) {
      console.error('Profile fetch error:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/stats?token=${token}`);
      setStats(response.data);
    } catch (error) {
      console.error('Stats fetch error:', error);
    }
  };

  const fetchRecentProfiles = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/matches?token=${token}&limit=6`);
      setRecentProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Profiles fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Shield,
      title: 'Verified Profiles',
      description: 'Phone, Aadhaar & Selfie verified members',
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      icon: Heart,
      title: 'Traditional Values',
      description: 'Respectful, family-oriented matchmaking',
      color: 'text-red-600',
      bg: 'bg-red-50'
    },
    {
      icon: Star,
      title: 'Horoscope Matching',
      description: 'Integrated Kundali compatibility check',
      color: 'text-amber-600',
      bg: 'bg-amber-50'
    },
    {
      icon: Users,
      title: 'Family Involvement',
      description: 'Create profiles for family members',
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-rose-600 to-amber-600 opacity-90"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}></div>
        
        <div className="relative max-w-6xl mx-auto px-4 py-16 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Sacred Matrimony Platform</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Noto Serif, serif' }}>
            धर्मविवाह
          </h1>
          <p className="text-xl md:text-2xl mb-2 font-medium">DharmVivah</p>
          <p className="text-rose-100 text-lg mb-8 max-w-2xl mx-auto">
            Find your life partner with traditional values and modern convenience. 
            A respectful matrimony service for dharmic families.
          </p>
          
          {!user ? (
            <button
              onClick={() => navigate('/auth')}
              className="px-8 py-4 bg-white text-rose-600 font-semibold rounded-xl hover:shadow-lg transition-all text-lg"
              data-testid="vivah-login-btn"
            >
              Login to Find Your Match
            </button>
          ) : !myProfile ? (
            <button
              onClick={() => navigate('/vivah/register')}
              className="px-8 py-4 bg-white text-rose-600 font-semibold rounded-xl hover:shadow-lg transition-all text-lg flex items-center gap-2 mx-auto"
              data-testid="create-profile-btn"
            >
              <UserPlus className="w-5 h-5" />
              Create Your Profile
            </button>
          ) : (
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => navigate('/vivah/my-profile')}
                className="px-5 py-3 bg-white text-rose-600 font-semibold rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
                data-testid="my-profile-btn"
              >
                <User className="w-5 h-5" />
                My Profile
              </button>
              <button
                onClick={() => navigate('/vivah/matches')}
                className="px-5 py-3 bg-white text-rose-600 font-semibold rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
                data-testid="browse-matches-btn"
              >
                <Search className="w-5 h-5" />
                Matches
              </button>
              <button
                onClick={() => navigate('/vivah/chat')}
                className="px-5 py-3 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-xl hover:bg-white/30 transition-all flex items-center gap-2"
                data-testid="chat-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Chat
              </button>
              <button
                onClick={() => navigate('/vivah/interests')}
                className="px-5 py-3 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-xl hover:bg-white/30 transition-all flex items-center gap-2"
                data-testid="view-interests-btn"
              >
                <Heart className="w-5 h-5" />
                Interests
                {stats?.pending_interests > 0 && (
                  <span className="bg-amber-400 text-amber-900 text-xs px-2 py-0.5 rounded-full">
                    {stats.pending_interests}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stats Section (for logged-in users with profile) */}
      {myProfile && stats && (
        <div className="max-w-4xl mx-auto px-4 -mt-8 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-xl bg-rose-50">
              <Eye className="w-6 h-6 text-rose-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-rose-600">{stats.profile_views}</p>
              <p className="text-sm text-gray-600">Profile Views</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-amber-50">
              <Heart className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-600">{stats.interests_received}</p>
              <p className="text-sm text-gray-600">Interests Received</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-green-50">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{stats.accepted_matches}</p>
              <p className="text-sm text-gray-600">Accepted Matches</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-blue-50">
              <Star className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-600">{stats.shortlisted_by}</p>
              <p className="text-sm text-gray-600">Shortlisted By</p>
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-12">
          Why Choose DharmVivah?
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className={`w-14 h-14 ${feature.bg} rounded-xl flex items-center justify-center mb-4`}>
                <feature.icon className={`w-7 h-7 ${feature.color}`} />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Profiles (for logged-in users) */}
      {myProfile && recentProfiles.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 pb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Recommended Matches</h2>
            <button
              onClick={() => navigate('/vivah/matches')}
              className="text-rose-600 font-medium flex items-center gap-1 hover:gap-2 transition-all"
            >
              View All <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentProfiles.map((profile) => (
              <div
                key={profile.id}
                onClick={() => navigate(`/vivah/profile/${profile.id}`)}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="relative h-48 bg-gradient-to-br from-rose-100 to-amber-100">
                  {profile.profile_photo ? (
                    <img 
                      src={profile.profile_photo} 
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center">
                        <span className="text-4xl font-bold text-rose-300">
                          {profile.name?.charAt(0)}
                        </span>
                      </div>
                    </div>
                  )}
                  {profile.is_verified && (
                    <div className="absolute top-3 right-3 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <Shield className="w-3 h-3" /> Verified
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-gray-800 group-hover:text-rose-600 transition-colors">
                        {profile.name}, {profile.age}
                      </h3>
                      <p className="text-sm text-gray-500">{profile.height} • {profile.city}</p>
                    </div>
                    <span className="text-xs text-gray-400">{profile.profile_id}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-rose-50 text-rose-700 px-2 py-1 rounded-full">
                      {profile.religion}
                    </span>
                    <span className="bg-amber-50 text-amber-700 px-2 py-1 rounded-full">
                      {profile.profession}
                    </span>
                    <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                      {profile.education}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA Section */}
      {!myProfile && (
        <div className="bg-gradient-to-r from-rose-600 to-amber-600 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Begin Your Sacred Journey</h2>
            <p className="text-rose-100 mb-8 max-w-2xl mx-auto">
              Join thousands of dharmic families who found their perfect match through DharmVivah. 
              Create your profile today and take the first step towards a blessed union.
            </p>
            <button
              onClick={() => user ? navigate('/vivah/register') : navigate('/auth')}
              className="px-8 py-4 bg-white text-rose-600 font-semibold rounded-xl hover:shadow-lg transition-all text-lg"
            >
              {user ? 'Create Your Profile' : 'Get Started Free'}
            </button>
          </div>
        </div>
      )}

      {/* Trust Indicators */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <p className="text-3xl font-bold text-rose-600">10,000+</p>
            <p className="text-gray-600">Verified Profiles</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-amber-600">5,000+</p>
            <p className="text-gray-600">Successful Matches</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-green-600">100%</p>
            <p className="text-gray-600">Privacy Protection</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-blue-600">24/7</p>
            <p className="text-gray-600">Support Available</p>
          </div>
        </div>
      </div>
    </div>
  );
}
